import { useCallback, useEffect, useRef, useState } from 'react'
import { Mic, MicStop } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { DictationFailure, DictationSurface } from '@/analytics/enums/dictation.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { markDictationUsed } from '@/analytics/utils/dictation-analytics'
import { postTranscribeAudio } from '@/transcription/fetch/post-transcribe-audio'

type VoiceInputButtonProps = {
  disabled: boolean
  onTranscription: (text: string) => void
  dictationSurface: DictationSurface
  conversationId?: string
  composerHadText: boolean
}

function conversationProperty(conversationId: string | undefined): { conversationId: string } | undefined {
  if (!conversationId) return undefined
  return { conversationId }
}

type VoiceState = 'idle' | 'acquiring' | 'recording' | 'transcribing' | 'error'

type RecorderFormat = {
  mimeType: string
  filename: string
}

// Chromium records webm/opus; Safari only records mp4/aac. Order is preference, not support.
const kRecorderFormats: RecorderFormat[] = [
  { mimeType: 'audio/webm;codecs=opus', filename: 'recording.webm' },
  { mimeType: 'audio/mp4', filename: 'recording.mp4' },
]

const kErrorResetMs = 3000

function selectRecorderFormat(): RecorderFormat | null {
  if (typeof MediaRecorder === 'undefined') return null
  return kRecorderFormats.find((format) => MediaRecorder.isTypeSupported(format.mimeType)) ?? null
}

export default function VoiceInputButton({ disabled, onTranscription, dictationSurface, conversationId, composerHadText }: VoiceInputButtonProps) {
  const { trackEvent } = useAnalytics()
  const [isSupported] = useState(() => selectRecorderFormat() !== null)
  const [voiceState, setVoiceState] = useState<VoiceState>('idle')
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const errorResetTimerRef = useRef<number | null>(null)
  // `getUserMedia` and the transcription upload both outlive a single click. These refs track the
  // in-flight work so a second click cannot start a parallel recorder and unmount can cancel it.
  const isMountedRef = useRef(true)
  const isAcquiringRef = useRef(false)
  const uploadAbortRef = useRef<AbortController | null>(null)

  // `recorder.onstop` is bound once when recording starts, so it must reach the latest callback
  // rather than the one captured at that moment. The composer appends to the text typed so far,
  // and the user can keep typing while dictating.
  const onTranscriptionRef = useRef(onTranscription)
  const analyticsRef = useRef({ trackEvent, dictationSurface, conversationId, composerHadText })
  useEffect(() => {
    onTranscriptionRef.current = onTranscription
  }, [onTranscription])
  useEffect(() => {
    analyticsRef.current = { trackEvent, dictationSurface, conversationId, composerHadText }
  }, [composerHadText, conversationId, dictationSurface, trackEvent])

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const clearErrorResetTimer = useCallback(() => {
    if (errorResetTimerRef.current === null) return
    window.clearTimeout(errorResetTimerRef.current)
    errorResetTimerRef.current = null
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      // Nothing may write text into a composer that is gone: the conversation can be switched
      // while a clip is still uploading, and its draft may have moved on by the time we answer.
      isMountedRef.current = false
      uploadAbortRef.current?.abort()
      uploadAbortRef.current = null
      // Drop the handlers first so an in-flight recording cannot upload or write text after unmount.
      const recorder = recorderRef.current
      if (recorder) {
        recorder.ondataavailable = null
        recorder.onstop = null
        recorderRef.current = null
      }
      releaseStream()
      if (errorResetTimerRef.current !== null) window.clearTimeout(errorResetTimerRef.current)
    }
  }, [releaseStream])

  const flagError = useCallback(
    (failure: DictationFailure, durationMs?: number) => {
      setVoiceState('error')
      clearErrorResetTimer()
      errorResetTimerRef.current = window.setTimeout(() => {
        errorResetTimerRef.current = null
        setVoiceState('idle')
      }, kErrorResetMs)
      const analytics = analyticsRef.current
      analytics.trackEvent(AnalyticsEvent.DictationFailed, {
        dictationSurface: analytics.dictationSurface,
        dictationFailure: failure,
        ...conversationProperty(analytics.conversationId),
        ...(durationMs === undefined ? {} : { durationMs }),
      })
    },
    [clearErrorResetTimer]
  )

  const uploadRecording = useCallback(
    async (blob: Blob, filename: string, durationMs: number, composerHadTextAtStart: boolean) => {
      const controller = new AbortController()
      uploadAbortRef.current = controller
      setVoiceState('transcribing')
      try {
        const text = await postTranscribeAudio(blob, filename, controller.signal)
        if (!isMountedRef.current) return
        if (text) {
          onTranscriptionRef.current(text)
          const analytics = analyticsRef.current
          if (analytics.conversationId) markDictationUsed(analytics.conversationId)
          analytics.trackEvent(AnalyticsEvent.DictationCompleted, {
            dictationSurface: analytics.dictationSurface,
            composerHadText: composerHadTextAtStart,
            durationMs,
            ...conversationProperty(analytics.conversationId),
          })
        }
        setVoiceState('idle')
      } catch {
        if (!isMountedRef.current) return
        flagError(DictationFailure.transcription, durationMs)
      } finally {
        if (uploadAbortRef.current === controller) uploadAbortRef.current = null
      }
    },
    [flagError]
  )

  const startRecording = useCallback(async () => {
    // A pending reset from an earlier failure would otherwise flip this back to idle mid-recording.
    clearErrorResetTimer()

    // State updates are async, so the ref is what actually rejects a second click that lands
    // while the browser is still showing the permission prompt.
    if (isAcquiringRef.current) return

    const format = selectRecorderFormat()
    if (!format) {
      flagError(DictationFailure.permissionOrDevice)
      return
    }

    isAcquiringRef.current = true
    setVoiceState('acquiring')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Unmounted while the prompt was open: nothing left can stop this stream, so release it
      // rather than leaving the microphone open with no control.
      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      const recorder = new MediaRecorder(stream, { mimeType: format.mimeType })
      streamRef.current = stream
      recorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }

      const composerHadTextAtStart = analyticsRef.current.composerHadText
      const startedAt = Date.now()
      recorder.onstop = () => {
        releaseStream()
        recorderRef.current = null
        const blob = new Blob(chunksRef.current, { type: format.mimeType })
        chunksRef.current = []
        const durationMs = Date.now() - startedAt
        if (blob.size > 0) {
          void uploadRecording(blob, format.filename, durationMs, composerHadTextAtStart)
          return
        }
        setVoiceState('idle')
      }

      recorder.start()
      setVoiceState('recording')
      const analytics = analyticsRef.current
      analytics.trackEvent(AnalyticsEvent.DictationStarted, {
        dictationSurface: analytics.dictationSurface,
        composerHadText: composerHadTextAtStart,
        ...conversationProperty(analytics.conversationId),
      })
    } catch {
      releaseStream()
      flagError(DictationFailure.permissionOrDevice)
    } finally {
      isAcquiringRef.current = false
    }
  }, [clearErrorResetTimer, flagError, releaseStream, uploadRecording])

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') {
      releaseStream()
      setVoiceState('idle')
      return
    }
    // `onstop` owns the upload, so the button lands in the transcribing state from there.
    recorder.stop()
  }, [releaseStream])

  if (!isSupported) return null

  const isRecording = voiceState === 'recording'
  const isAcquiring = voiceState === 'acquiring'
  const isTranscribing = voiceState === 'transcribing'
  const hasError = voiceState === 'error'

  // Never disable this state: submitting a message flips `disabled` on, and an unstoppable
  // recorder would hold the microphone open.
  if (isRecording) {
    return (
      <IconButton
        aria-label="Stop dictating"
        icon={<MicStop />}
        onClick={stopRecording}
        size="lg"
        tooltipText="Stop dictating"
        type="button"
        variant="filled"
      />
    )
  }

  return (
    <IconButton
      aria-label={isTranscribing ? 'Transcribing dictation' : 'Dictate a message'}
      color="neutral"
      disabled={disabled}
      icon={<Mic />}
      loading={isTranscribing || isAcquiring}
      onClick={startRecording}
      size="lg"
      tooltipText={hasError ? 'Dictation failed. Try again.' : 'Dictate a message'}
      type="button"
      variant="plain"
    />
  )
}
